import { Editor } from "@monaco-editor/react";
import GitHubIcon from "@mui/icons-material/GitHub";
import { Box, Button, Card, CardContent, Chip, Link, Stack, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { Link as RouterLink } from "react-router";
import { useQueryParams } from "../../hooks/use-query-params";
import { BRAND_PRIMARY } from "../../theme/theme";

const ARGUS_ACTION_REPO = "https://github.com/desplega-ai/argus-action";

const QUICK_START_YAML = `# .github/workflows/argus-smoke.yaml
name: Argus smoke
on:
  pull_request:

jobs:
  argus:
    runs-on: ubuntu-latest
    permissions:
      contents: read
      pull-requests: write
    steps:
      - uses: actions/checkout@v4
      - uses: desplega-ai/argus-action@v1
        with:
          scenario: smoke-login
          base_url: https://preview.example.com/pr-\${{ github.event.pull_request.number }}
        env:
          ARGUS_API_KEY: \${{ secrets.ARGUS_API_KEY }}`;

/**
 * "Argus" tab inside `/configuration` — surfaces the public
 * `desplega-ai/argus-action` GitHub Action (v1, MIT). Shows a short
 * description, the minimal quick-start workflow, and a link to the repo.
 *
 * Visual layout mirrors the sibling `CITab` (`ci-tab.tsx`) — same Card +
 * `h5` heading + bordered Editor + right-aligned Copy button — so the
 * two feel like peer sections.
 *
 * A "Back to Argus session" button only appears when BOTH
 * `argus_session_id` and `argus_instance_id` query params are present,
 * which happens when the user lands here from an Argus session link
 * (see `be/core/argus/workspace.py`). During normal settings nav the
 * button is hidden.
 */
export const ArgusTab = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { getParam } = useQueryParams();
  const argusSessionId = getParam("argus_session_id");
  const argusInstanceId = getParam("argus_instance_id");
  const cameFromArgusSession = Boolean(argusSessionId && argusInstanceId);

  const handleCopyYaml = () => {
    navigator.clipboard.writeText(QUICK_START_YAML);
    enqueueSnackbar("YAML copied to clipboard", {
      variant: "success",
      anchorOrigin: { horizontal: "right", vertical: "bottom" },
    });
  };

  return (
    <Stack data-testid="configuration-argus-tab" spacing={2}>
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <GitHubIcon sx={{ fontSize: 22, color: "text.secondary" }} />
            <Typography variant="h5" component="div">
              Argus GitHub Action
            </Typography>
            <Chip
              label="v1 · Available"
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: "0.7rem",
                borderColor: BRAND_PRIMARY,
                color: BRAND_PRIMARY,
              }}
            />
          </Stack>
          <Typography variant="body2" mt={1}>
            Run an Argus agent against any URL on pull requests and post the report as a sticky PR
            comment. One action covers both deterministic smoke tests (fail the check on non-pass)
            and exploratory "try X, tell us if anything looks broken" runs.
          </Typography>
          <Typography variant="body2" mt={1}>
            Add an <code>ARGUS_API_KEY</code> repository secret (generate one in the{" "}
            <Link component={RouterLink} to="/configuration?tab=api">
              API keys tab
            </Link>
            ) and grant the consuming workflow <code>pull-requests: write</code> so the action can
            upsert the PR comment.
          </Typography>
          <Box mt={2} border={1} borderColor="divider" p={1} borderRadius={1}>
            <Editor
              height="350px"
              defaultLanguage="yaml"
              value={QUICK_START_YAML}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
              }}
            />
            <Stack direction="row" spacing={2} mt={1} alignItems="center" justifyContent="flex-end">
              {cameFromArgusSession && argusSessionId && (
                <Button
                  component={RouterLink}
                  to={`/argus?session=${encodeURIComponent(argusSessionId)}`}
                  size="small"
                  variant="text"
                  sx={{ marginTop: 1, color: "text.secondary" }}
                >
                  ← Back to Argus session
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={handleCopyYaml}
                sx={{ marginTop: 1 }}
              >
                Copy code
              </Button>
            </Stack>
          </Box>
          <Typography variant="body2" mt={1} color="text.secondary">
            Only <code>scenario</code> and <code>base_url</code> are required. See the{" "}
            <Link href={ARGUS_ACTION_REPO} target="_blank" rel="noopener">
              action repository
            </Link>{" "}
            for inputs, outputs, prompt templates, and preview-deployment recipes (Vercel, Netlify,
            Cloudflare Pages, Fly.io, Render, Railway).
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
};
